import { Eye, EyeOff, Home, Lock, Mail } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { AuthError } from '@/services/auth.service';
import { useAuthStore } from '@/stores/useAuthStore';

export function AdminLoginPage() {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({
    email: '',
    password: '',
  });

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Reset errors
    setErrors({ email: '', password: '' });

    // Validation
    let hasErrors = false;
    const newErrors = { email: '', password: '' };

    if (!formData.email) {
      newErrors.email = 'Email is required';
      hasErrors = true;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
      hasErrors = true;
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
      hasErrors = true;
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    // Call real API
    setIsSubmitting(true);
    try {
      await login({
        email: formData.email,
        password: formData.password,
      });
      navigate('/dashboard');
    } catch (error) {
      if (error instanceof AuthError) {
        switch (error.code) {
          case 'INVALID_CREDENTIALS':
            setErrors({
              email: '',
              password: 'Invalid email or password',
            });
            break;
          case 'NOT_ADMIN':
            setErrors({
              email: '',
              password: 'Access denied. Admin privileges required.',
            });
            break;
          case 'NETWORK_ERROR':
            setErrors({
              email: '',
              password: 'Unable to connect. Please try again.',
            });
            break;
          default:
            setErrors({
              email: '',
              password: error.message || 'An error occurred. Please try again.',
            });
        }
      } else {
        setErrors({
          email: '',
          password: 'An unexpected error occurred. Please try again.',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange =
    (field: 'email' | 'password') => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData({ ...formData, [field]: e.target.value });
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors({ ...errors, [field]: '' });
      }
    };

  return (
    <div className="flex min-h-screen bg-neutral-50">
      {/* Left Panel - Login Form */}
      <div className="flex w-full flex-col lg:w-[52%]">
        <div className="flex min-h-screen flex-col px-8 py-12 sm:px-12 sm:py-16 lg:px-24 xl:px-32">
          {/* Logo */}
          <div className="mb-16 flex items-center gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-emerald-900 shadow-md">
              <Home className="h-7 w-7 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-neutral-900">TrueHaus</h2>
              <p className="text-sm text-neutral-500">Admin Portal</p>
            </div>
          </div>

          {/* Login Form Container */}
          <div className="flex flex-1 flex-col justify-center -mt-4">
            <div className="mx-auto w-full max-w-lg">
              {/* Welcome back heading */}
              <h1 className="mb-6 text-4xl font-bold tracking-tight text-neutral-900">
                Welcome back
              </h1>

              {/* Description text */}
              <p className="mb-10 text-base leading-relaxed text-neutral-600">
                Sign in to your admin account to manage properties and contractors.
              </p>

              <form onSubmit={handleSubmit}>
                {/* Email Address label */}
                <label
                  htmlFor="email"
                  className="mb-3 block text-sm font-semibold text-neutral-700"
                >
                  Email Address
                </label>

                {/* Email input */}
                <div className="relative mb-8">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                    <Mail className="h-5 w-5 text-neutral-400" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    placeholder="admin@truehaus.com"
                    className={`w-full rounded-lg border bg-white py-4 pl-12 pr-4 text-neutral-900 placeholder-neutral-400 shadow-sm transition-colors focus:outline-none focus:ring-2 ${
                      errors.email
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                        : 'border-neutral-200 focus:border-emerald-500 focus:ring-emerald-500/20'
                    }`}
                  />
                  {errors.email && <p className="mt-1.5 text-sm text-red-600">{errors.email}</p>}
                </div>

                {/* Password label */}
                <label
                  htmlFor="password"
                  className="mb-3 block text-sm font-semibold text-neutral-700"
                >
                  Password
                </label>

                {/* Password input */}
                <div className="relative mb-8">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                    <Lock className="h-5 w-5 text-neutral-400" />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleInputChange('password')}
                    placeholder="Enter your password"
                    className={`w-full rounded-lg border bg-white py-4 pl-12 pr-12 text-neutral-900 placeholder-neutral-400 shadow-sm transition-colors focus:outline-none focus:ring-2 ${
                      errors.password
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                        : 'border-neutral-200 focus:border-emerald-500 focus:ring-emerald-500/20'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-4 text-neutral-400 hover:text-neutral-600"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                  {errors.password && (
                    <p className="mt-1.5 text-sm text-red-600">{errors.password}</p>
                  )}
                </div>

                {/* Sign In button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full rounded-lg bg-emerald-900 py-4.5 font-semibold text-white shadow-md transition-all hover:bg-emerald-800 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSubmitting ? 'Signing in...' : 'Sign In'}
                </button>
              </form>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-16 text-center text-sm text-neutral-500">
            &copy; {`${new Date().getFullYear()} TrueHaus. All rights reserved.`}
          </div>
        </div>
      </div>

      {/* Right Panel - Marketing Content */}
      <div className="hidden lg:flex lg:w-[48%]">
        <div className="relative flex w-full flex-col justify-center bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-900 px-8 py-20 lg:px-12 xl:px-20">
          {/* Content Container */}
          <div className="mx-auto w-full max-w-2xl pt-16">
            {/* Hero Image Card */}
            <div className="mb-12 overflow-hidden rounded-2xl shadow-2xl">
              <div className="aspect-[16/10] w-full bg-gradient-to-br from-neutral-200 to-neutral-300">
                {/* Placeholder for property image - replace with actual image */}
                <img
                  src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80"
                  alt="Modern luxury property"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>

            {/* Marketing Content */}
            <div className="space-y-8 text-center">
              <h2 className="text-3xl font-bold leading-tight text-white xl:text-4xl">
                Manage Your Property Portfolio
              </h2>
              <p className="text-lg leading-relaxed text-emerald-100">
                Access powerful tools to create, edit, and manage property listings. Connect with
                contractors and oversee renovations from one centralized platform.
              </p>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-8 pt-12 lg:gap-12">
                <div className="space-y-3">
                  <div className="text-4xl font-bold text-white xl:text-5xl">150+</div>
                  <div className="text-sm font-medium text-emerald-200">Active Listings</div>
                </div>
                <div className="space-y-3">
                  <div className="text-4xl font-bold text-white xl:text-5xl">45</div>
                  <div className="text-sm font-medium text-emerald-200">Contractors</div>
                </div>
                <div className="space-y-3">
                  <div className="text-4xl font-bold text-white xl:text-5xl">98%</div>
                  <div className="text-sm font-medium text-emerald-200">Satisfaction</div>
                </div>
              </div>
            </div>
          </div>

          {/* Decorative Elements */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden opacity-20">
            <div className="absolute -right-20 -top-20 h-80 w-80 rounded-full bg-white blur-3xl" />
            <div className="absolute -bottom-20 -left-20 h-80 w-80 rounded-full bg-white blur-3xl" />
          </div>
        </div>
      </div>
    </div>
  );
}
